import { useListAssetGroupsQuery } from '@casops/store/apis/assetGroupsApi';
import { EmptyCell, GenericValueFormatterFunc } from './genericFormatters';
import {
  ListGeofenceStyleModel,
  useListGeofenceStylesQuery,
} from '@casops/store/apis/geofenceStylesApi';
import { useCallback } from 'react';
import { GeofenceTypeV2 } from '@casops/store/apis/geofencesApi';
import { useTranslation } from 'react-i18next';

export function useStyleIdFormatter(): GenericValueFormatterFunc<number | null> {
  const { data: styles } = useListGeofenceStylesQuery(undefined, {
    refetchOnMountOrArgChange: 120,
  });

  const formatter: GenericValueFormatterFunc<number | null> = useCallback(
    ({ value }) => {
      if (styles && typeof value === 'number') {
        // Attempt to find a match for the style ID.
        const styleMatch: ListGeofenceStyleModel | undefined = styles.find(
          ({ id }) => id === value,
        );

        if (styleMatch) {
          return styleMatch.name;
        }
      }

      return EmptyCell;
    },
    [styles],
  );

  return formatter;
}

export function useAssetGroupIdFormatter(): GenericValueFormatterFunc<number[]> {
  const { data: assetGroups } = useListAssetGroupsQuery(undefined, {
    refetchOnMountOrArgChange: 60,
  });

  const formatter: GenericValueFormatterFunc<number[]> = useCallback(
    ({ value }) => {
      if (assetGroups && value && Array.isArray(value)) {
        const assetGroupNames: string[] = [];

        value.forEach(id => {
          if (typeof id === 'number') {
            const match = assetGroups.find(g => g.id === id);
            if (match && match.name) {
              assetGroupNames.push(match.name);
            }
          }
        });

        if (assetGroupNames.length) {
          // Enforce alphabetical order.
          const sorted = assetGroupNames.sort((a, b) => a.localeCompare(b));
          return sorted.join(', ');
        }
      }

      return EmptyCell;
    },
    [assetGroups],
  );

  return formatter;
}

export function useGeofenceTypesFormatter(): GenericValueFormatterFunc<GeofenceTypeV2[]> {
  const { t } = useTranslation();

  const formatter: GenericValueFormatterFunc<GeofenceTypeV2[]> = useCallback(
    ({ value }) => {
      if (value && Array.isArray(value)) {
        const geofenceTypeNames = value.map(v => t(`GeofenceTypeV2.${v}`));

        if (geofenceTypeNames.length) {
          // Enforce alphabetical order.
          const sorted = geofenceTypeNames.sort((a, b) => a.localeCompare(b));
          return sorted.join(', ');
        }
      }

      return EmptyCell;
    },
    [t],
  );

  return formatter;
}
